/**
 * RequirementsReviewPhase — Phase 2.
 *
 * The agent has parsed each cybersecurity requirement and assigned a
 * fulfillment verdict (Met / Partially Met / Not Met) based on the
 * evidence the user attached in Phase 1. This screen lets the user
 * review the agent's verdicts and override any that disagree with
 * reality via the existing `RequirementEditModal` from
 * `features/athena/components/`.
 *
 * Each control's requirements render as a group under its control
 * header (matches the nested data shape the backend returns:
 * `data.controls[].requirements[]`). Once the user is comfortable,
 * "Continue to Attack Path Analysis" calls
 * `actions.continueWorkflow()` and the backend transitions to
 * `attack_path_analysis`.
 *
 * Read-only when the backend phase is no longer
 * `requirements_review` — past phases stay viewable from the rail.
 */
import React, { useMemo, useState } from 'react';
import { Button } from '../../../../../components/ui/Button2';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '../../../../../components/ui/Empty';
import { LoadingState } from '../../../../../components/ui/LoadingState';
import { TypographyH4, TypographyMuted } from '../../../../../components/ui/Typography';
import StatusPill from '../../../../../components/ui/StatusPill/StatusPill';
import RequirementEditModal from '../../../../athena/components/RequirementEditModal';
import AnalysisErrorCard from '../../AnalysisErrorCard/AnalysisErrorCard';
import type { ResidualActions } from '../../../hooks/useResidualProgress';
import type {
  ResidualControl,
  ResidualRequirement,
  ResidualState,
} from '../../../state/types';
import styles from './RequirementsReviewPhase.module.css';

// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;
const LoadingStateAny = LoadingState as unknown as React.ComponentType<any>;

interface RequirementsReviewPhaseProps {
  state: ResidualState;
  actions: ResidualActions;
}

type StatusKey = 'fulfilled' | 'partial' | 'not_fulfilled' | 'pending';

const normaliseStatus = (raw: string | undefined): StatusKey => {
  if (!raw) return 'pending';
  // Backend emits fulfilled / partially_fulfilled / not_fulfilled (and the
  // enhanced prompt uppercases them). Check the more-specific substrings
  // first: `partially_fulfilled` contains "fulfilled" and `not_fulfilled`
  // contains "fulfilled", so a `fulfilled`-first check would mis-map both.
  const v = raw.toLowerCase();
  if (v.includes('not')) return 'not_fulfilled';
  if (v.includes('partial')) return 'partial';
  if (v.includes('fulfilled') || v.includes('met')) return 'fulfilled';
  return 'pending';
};

const STATUS_LABEL: Record<StatusKey, string> = {
  fulfilled: 'Met',
  partial: 'Partially Met',
  not_fulfilled: 'Not Met',
  pending: 'Pending',
};

const STATUS_TONE: Record<StatusKey, 'success' | 'warning' | 'error' | 'neutral'> = {
  fulfilled: 'success',
  partial: 'warning',
  not_fulfilled: 'error',
  pending: 'neutral',
};

export default function RequirementsReviewPhase({
  state,
  actions,
}: RequirementsReviewPhaseProps) {
  const controls = state.data?.controls ?? [];
  const editable = state.backendPhase === 'requirements_review';
  const isEvaluating = state.backendPhase === 'requirements_evaluation';
  const evidence = state.evidenceStatus as
    | {
        collection_status?: string;
        controls_processed?: number;
        total_evidence?: number;
      }
    | null;

  const [selected, setSelected] = useState<{
    requirement: ResidualRequirement;
    controlId: string;
  } | null>(null);
  const [isContinuing, setIsContinuing] = useState(false);

  const stats = useMemo(() => {
    let total = 0;
    let met = 0;
    let partial = 0;
    let notMet = 0;
    for (const c of controls) {
      for (const r of c.requirements ?? []) {
        total += 1;
        const s = normaliseStatus(r.fulfillment_status);
        if (s === 'fulfilled') met += 1;
        else if (s === 'partial') partial += 1;
        else if (s === 'not_fulfilled') notMet += 1;
      }
    }
    return { total, met, partial, notMet };
  }, [controls]);

  const handleSave = async (updateData: unknown) => {
    if (!selected) return;
    const reqId = selected.requirement.req_id ?? selected.requirement.requirement_id;
    if (!reqId) return;
    // On failure the error propagates to the modal (which surfaces it via
    // its own state) and the modal stays open — only success closes it.
    await actions.updateRequirement(reqId, updateData);
    setSelected(null);
  };

  const handleContinue = async () => {
    if (isContinuing) return;
    setIsContinuing(true);
    try {
      await actions.continueWorkflow();
    } finally {
      setIsContinuing(false);
    }
  };

  return (
    <div className={styles.phase}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <TypographyH4>Requirements Review</TypographyH4>
          <TypographyMuted className={styles.subtitle}>
            The agent has classified each requirement&rsquo;s fulfillment
            against the evidence attached in the previous phase. Review
            its verdicts and override any that don&rsquo;t match reality.
          </TypographyMuted>
        </div>
        <div className={styles.statsBlock}>
          <span className={styles.statsItem}>
            <strong>{stats.met}</strong> Met
          </span>
          <span className={styles.statsItem}>
            <strong>{stats.partial}</strong> Partial
          </span>
          <span className={styles.statsItem}>
            <strong>{stats.notMet}</strong> Not Met
          </span>
          <span className={styles.statsTotal}>
            of {stats.total} requirement{stats.total === 1 ? '' : 's'}
          </span>
        </div>
      </header>

      {isEvaluating ? (
        state.analysisError ? (
          <AnalysisErrorCard
            title="Requirements evaluation failed"
            message={state.analysisError}
            onRetry={actions.continueWorkflow}
          />
        ) : (
        <LoadingStateAny
          variant="page"
          title="Evaluating requirements&hellip;"
          description="The agent is collecting evidence and assigning a fulfillment verdict (Met / Partially Met / Not Met) to each requirement. This usually finishes in under a minute."
        >
          {evidence?.controls_processed != null && (
            <div className={styles.spinnerProgress}>
              Evidence processed for{' '}
              <strong>{evidence.controls_processed}</strong> control
              {evidence.controls_processed === 1 ? '' : 's'}
              {evidence.total_evidence != null
                ? ` · ${evidence.total_evidence} item${
                    evidence.total_evidence === 1 ? '' : 's'
                  }`
                : ''}
            </div>
          )}
        </LoadingStateAny>
        )
      ) : controls.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No requirements yet</EmptyTitle>
            <EmptyDescription>
              No requirements available yet for this session.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className={styles.controlList}>
          {controls.map((c) => (
            <ControlGroup
              key={c.control_id}
              control={c}
              editable={editable}
              onEdit={(req) =>
                setSelected({ requirement: req, controlId: c.control_id })
              }
            />
          ))}
        </div>
      )}

      {editable && (
        <footer className={styles.footer}>
          <ButtonAny
            size="lg"
            loading={isContinuing}
            onClick={handleContinue}
            disabled={isContinuing || stats.total === 0}
          >
            {isContinuing ? 'Sending…' : 'Continue to Attack Path Analysis'}
          </ButtonAny>
        </footer>
      )}

      {selected && (
        <RequirementEditModal
          isOpen={!!selected}
          onClose={() => setSelected(null)}
          requirement={selected.requirement}
          controlId={selected.controlId}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

/* ============================================================
   Per-control group — header strip + nested requirement rows.
   ============================================================ */
function ControlGroup({
  control,
  editable,
  onEdit,
}: {
  control: ResidualControl;
  editable: boolean;
  onEdit: (req: ResidualRequirement) => void;
}) {
  const reqs = control.requirements ?? [];
  if (reqs.length === 0) return null;

  return (
    <section className={styles.controlGroup}>
      <header className={styles.controlHeader}>
        <span className={styles.controlIdChip}>{control.control_id}</span>
        {control.control_name && (
          <span className={styles.controlName}>{control.control_name}</span>
        )}
        <span className={styles.controlReqCount}>
          {reqs.length} requirement{reqs.length === 1 ? '' : 's'}
        </span>
      </header>
      <ul className={styles.reqList}>
        {reqs.map((r) => (
          <RequirementRow
            key={r.req_id ?? r.requirement_id}
            requirement={r}
            editable={editable}
            onEdit={() => onEdit(r)}
          />
        ))}
      </ul>
    </section>
  );
}

/* ============================================================
   Single requirement row.
   ============================================================ */
function RequirementRow({
  requirement,
  editable,
  onEdit,
}: {
  requirement: ResidualRequirement;
  editable: boolean;
  onEdit: () => void;
}) {
  const status = normaliseStatus(requirement.fulfillment_status);
  const reqId = requirement.req_id ?? requirement.requirement_id;
  const text =
    (requirement as { req_statement?: string }).req_statement ??
    (requirement as { req_title?: string }).req_title ??
    requirement.requirement_text;
  const rationale =
    requirement.agent_rationale ??
    (requirement as { rationale?: string }).rationale;
  // What the verdict rests on. Previously a Met/Not-Met verdict appeared with no
  // traceable basis at all, so reviewers couldn't tell whether evidence was read.
  const evidenceBasis = requirement.evidence_basis;
  const needsReview = requirement.needs_review === true;

  return (
    <li className={styles.reqRow}>
      <div className={styles.reqMain}>
        <div className={styles.reqHeader}>
          {reqId && <span className={styles.reqId}>{reqId}</span>}
          <StatusPill size="sm" tone={STATUS_TONE[status]} label={STATUS_LABEL[status]} />
          {needsReview && (
            <StatusPill
              size="sm"
              tone="warning"
              label="Needs review — no evidence"
            />
          )}
        </div>
        {text && <div className={styles.reqText}>{text}</div>}
        {rationale && (
          <div className={styles.reqRationale}>
            <span className={styles.reqRationaleLabel}>Agent rationale:</span>{' '}
            {rationale}
          </div>
        )}
        {evidenceBasis && (
          <div className={styles.reqRationale}>
            <span className={styles.reqRationaleLabel}>Based on:</span>{' '}
            {evidenceBasis}
          </div>
        )}
      </div>
      <div className={styles.reqActions}>
        <ButtonAny variant={editable ? 'default' : 'outline'} size="sm" onClick={onEdit}>
          {editable ? 'Edit' : 'View'}
        </ButtonAny>
      </div>
    </li>
  );
}
